import { useContext, useEffect } from 'react'
import * as H from 'history'
import { ContentBody, ContentHeader } from 'components/shared/content'
import { ActionTableCell, ExTable, SelectionFixedFooter, TableBody, TableHead, TableRow, TableWrapper, useMultipleCheckForm } from 'components/shared/widget'
import { useFetchProtocolVersionsApi } from 'api/protocol_version'
import { ProtocolVersion, ProtocolVersionSearchForm } from 'entities/protocol_version'
import { Pagination } from 'components/shared/pagination'
import { useForm, useQuery } from 'utils/hooks'
import { SearchConditionView } from 'components/shared/search_drawer'
import { GlobalStateContext } from 'contexts/global_state_context'
import { protocolVersionColumnInfoList, ProtocolVersionTableData } from './protocol_version_table'
import { TableHeads, useDisplaySetting } from 'components/shared/display_setting'
import { useState } from 'react'
import { Flex } from 'components/shared/flex'
import { EditProtocolVersionForm, NewProtocolVersionForm } from './protocol_version_form'
import { ProtocolVersionProbesModal } from './protocol_version_probes_modal'

type Props = {
    headerTitle?: string
    onlyQC?: boolean
    history: H.History
    route?: string
}

const ProtocolVersions = (props: Props) => {
    const globalState = useContext(GlobalStateContext)
    const query = useQuery<ProtocolVersionSearchForm>()
    const searchForm = useForm<ProtocolVersionSearchForm>({ ...query, onlyQc: props.onlyQC, ordering: 'protocol_version_number' })
    const api = useFetchProtocolVersionsApi(searchForm)
    const checkIdForm = useMultipleCheckForm<ProtocolVersion>()
    const protocolVersionDisplaySetting = useDisplaySetting(protocolVersionColumnInfoList(searchForm), 'ProtocolVersion')
    const [selectedProtocolVersion, setSelectedProtocolVersion] = useState<ProtocolVersion>({})
    const [showEdit, setShowEdit] = useState<boolean>(false)
    const [showProbes, setShowProbes] = useState<boolean>(false)

    useEffect(() => {
        api.execute()
    }, [])

    const handleClickRemoveFilter = (key: keyof ProtocolVersionSearchForm): void => {
        searchForm.update((f) => {
            delete f[key]
        })
    }

    const handleClickEdit = (protocolVersion: ProtocolVersion) => {
        setSelectedProtocolVersion(protocolVersion)
        setShowEdit(true)
    }

    return (
        <>
            <ProtocolVersionProbesModal protocolVersion={selectedProtocolVersion} show={showProbes} onClose={() => setShowProbes(false)} />
            <EditProtocolVersionForm
                protocolVersion={selectedProtocolVersion}
                show={showEdit}
                onClose={() => {
                    setShowEdit(false)
                }}
                onComplete={() => {
                    api.execute()
                    setShowEdit(false)
                }}
                isStandardQc={searchForm.object.isStandardQc || props.onlyQC}
            />
            <ContentHeader
                headerTitle={props.headerTitle ? props.headerTitle : 'プロトコルバージョン'}
                left={
                    <>
                        <NewProtocolVersionForm
                            onComplete={() => api.execute()}
                            productItemId={searchForm.object.productItemId}
                            isStandardQc={searchForm.object.isStandardQc || props.onlyQC}
                        />
                        <SearchConditionView
                            searchForm={searchForm}
                            onClickRemoveButton={handleClickRemoveFilter}
                            hiddenSearchParams={['onlyQc', 'productItemId', 'isStandardQc']}
                        />
                    </>
                }
            />
            <Flex style={{ marginTop: 5 }} alignItems="center" justifyContent="space-between">
                <Flex alignItems="center"></Flex>
                <Flex alignItems="center">
                    <Pagination pageSet={api.pageSet} response={api.response}></Pagination>
                    {/* <div style={{ minWidth: 140, textAlign: 'right' }}>総件数： {api.response.count.toLocaleString()}件</div> */}
                </Flex>
            </Flex>
            <ContentBody>
                <TableWrapper showFooter={checkIdForm.form.object.items.length > 0} contentHeaderCount={2}>
                    <ExTable className="sticky-table">
                        <TableHead>
                            <TableRow>
                                <ActionTableCell action='edit' th />
                                <TableHeads displaySetting={protocolVersionDisplaySetting} />
                            </TableRow>
                        </TableHead>
                        <TableBody loading={api.loading} columnLength={protocolVersionDisplaySetting.getColumnLength()}>
                            {api.response.results.map((protocolVersion: ProtocolVersion) => {
                                return (
                                    <tr key={'version-' + protocolVersion.id}>
                                        <ActionTableCell action="edit"
                                            onClick={() => {
                                                handleClickEdit(protocolVersion)
                                            }}
                                        />
                                        <ProtocolVersionTableData
                                            onClickProbes={() => {
                                                setSelectedProtocolVersion(protocolVersion)
                                                setShowProbes(true)
                                            }}
                                            protocolVersion={protocolVersion}
                                            displaySetting={protocolVersionDisplaySetting}
                                        />
                                    </tr>
                                )
                            })}
                        </TableBody>
                    </ExTable>
                    <Pagination pageSet={api.pageSet} response={api.response}></Pagination>
                </TableWrapper>
            </ContentBody>

            <SelectionFixedFooter
                ids={checkIdForm.ids()}
                onClickReset={() => checkIdForm.form.resetForm()}
                searchForm={searchForm}
                searchAttr="id"
                tableName="ProtocolVersion"
            ></SelectionFixedFooter>
        </>
    )
}

export default ProtocolVersions
